import FocusTrap from 'focus-trap-react';
import { CSSTransition } from 'react-transition-group';
import { Link } from 'react-router-dom';

import CloseX from 'components/icons/navigation/CloseX';

import * as Types from 'utilities/types';

const toggleBodyScroll = show => {
  if (show) {
    document.body.classList.add('overflow-hidden');
  } else {
    document.body.classList.remove('overflow-hidden');
  }
};

export const WIDTH_720 = 'w-720';
export const WIDTH_500 = 'w-500';

class Slideout extends React.Component {
  state = { lastFocusedEl: null };

  setLastFocusedEl = () => this.setState({ lastFocusedEl: document.activeElement });
  focusOnLastEl = () => {
    const { lastFocusedEl } = this.state;
    if (lastFocusedEl) setTimeout(() => lastFocusedEl.focus());
  };

  // Setting timeouts for event listeners to allow time for elements to receive/track focus
  addKeyDownListener = () =>
    setTimeout(() => document.addEventListener('keydown', this.checkDocumentKeyDown));

  removeKeyDownListener = () =>
    setTimeout(() => document.removeEventListener('keydown', this.checkDocumentKeyDown));

  onClose = () => {
    this.props.closeCallback();
    this.focusOnLastEl();
  };

  checkDocumentKeyDown = event => {
    if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
      const { backPath, history } = this.props;

      this.onClose();
      if (backPath) history.push(backPath);
    }
  };

  renderDefaultHeader() {
    const { backPath } = this.props;
    const textClassNames =
      'appearance-none border-none text-charcoal-100 hover:text-charcoal-80 focus:text-charcoal-80 bg-transparent';
    if (backPath)
      return (
        <Link
          to={backPath}
          onClick={this.onClose}
          className={textClassNames}
          aria-label="Close slideout"
        >
          <CloseX className="w-8 h-8" />
        </Link>
      );
    return (
      <button className={textClassNames} onClick={this.onClose} aria-label="Close slideout">
        <CloseX className="w-8 h-8" />
      </button>
    );
  }

  renderTopBar() {
    return (
      <nav className="px-6 py-4 flex justify-end items-center slideout-topBar">
        {this.props.headerElement}
        {this.renderDefaultHeader()}
      </nav>
    );
  }

  componentDidMount() {
    if (this.props.show) {
      this.setLastFocusedEl();
      this.addKeyDownListener();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.show && !prevProps.show) {
      this.setLastFocusedEl();
      this.addKeyDownListener();
    }
    if (!this.props.show && prevProps.show) this.removeKeyDownListener();
  }

  render() {
    const {
      id,
      labelId,
      show,
      backPath,
      children,
      desktopWidth,
      scrollableRef,
      slideoutContainerClassname,
    } = this.props;

    return (
      <FocusTrap
        active={show}
        focusTrapOptions={{
          onActivate: () => toggleBodyScroll(true),
          onDeactivate: () => toggleBodyScroll(false),
          returnFocusOnDeactivate: false,
        }}
      >
        <div
          id={id}
          aria-labelledby={labelId}
          role="dialog"
          aria-hidden={!show}
          className="react-modal"
        >
          <CSSTransition
            key="drawerSlideout"
            in={show}
            timeout={200}
            classNames="slideout"
            unmountOnExit
          >
            <section
              className={`slideout-container ${slideoutContainerClassname} ${
                desktopWidth === WIDTH_500 ? 'slideout-w-500' : 'slideout-w-720'
              }`}
              ref={scrollableRef}
            >
              {this.renderTopBar()}
              {children}
            </section>
          </CSSTransition>
          <CSSTransition
            key="overlaySlideout"
            in={show}
            timeout={200}
            classNames="fadeInOut"
            unmountOnExit
          >
            {backPath ? (
              <Link
                className="slideout-background"
                to={backPath}
                tabIndex="0"
                onClick={this.onClose}
              >
                <span>Close slideout</span>
              </Link>
            ) : (
              <button className="slideout-background" tabIndex="0" onClick={this.onClose}>
                <span>Close slideout</span>
              </button>
            )}
          </CSSTransition>
        </div>
      </FocusTrap>
    );
  }
}

Slideout.propTypes = {
  backPath: PropTypes.string,
  id: PropTypes.string.isRequired,
  labelId: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  closeCallback: PropTypes.func,
  desktopWidth: PropTypes.oneOf([WIDTH_720, WIDTH_500]),
  scrollableRef: Types.refType,
  slideoutContainerClassname: PropTypes.string,
};

Slideout.defaultProps = {
  backPath: '',
  closeCallback: () => {},
  desktopWidth: WIDTH_720,
  scrollableRef: () => {},
  slideoutContainerClassname: '',
};

export default Slideout;
